import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { FlowQueryModel } from "src/app/models/flowquerymodel";
import { BundleService } from "./bundleservice.service";

/**
 * Opens the guide pack editor (/create-guide-pack) for an existing pack by seeding the
 * localStorage state the editor reads on load.
 */
@Injectable({ providedIn: "root" })
export class BundleEditorLauncherService {
  constructor(private bundleService: BundleService, private router: Router) {}

  /** Loads the pack (its unpublished edits when there are any) and opens it in the editor. */
  openByRowKey(rowKey: string, userEmail: string, returnUrl?: string): Observable<boolean> {
    const query = new FlowQueryModel({ BundleId: rowKey, userEmail: userEmail || "" });
    return this.bundleService.getBundleById(query, true).pipe(
      map((data) => {
        const bundle = data?.bundleModel;
        if (!bundle) return false;
        this.open(bundle, returnUrl);
        return true;
      }),
      catchError(() => of(false))
    );
  }

  /** `returnUrl` is where closing the editor goes; defaults to the pack's page. */
  open(bundleData: any, returnUrl?: string): void {
    localStorage.removeItem("bundleData");
    localStorage.removeItem("bundleIdToBeDeleted");
    localStorage.removeItem("addbundleselectedflows");
    localStorage.removeItem("createbundledetails");

    localStorage.removeItem("editBundlesupportingDocumentHyperLinksIndex");
    localStorage.removeItem("BundleSupportingDocumentHyperLinksRequired");
    localStorage.removeItem("editBundleSupportingDocumentRequiredIndex");
    localStorage.removeItem("BundleSupportingDocumentDocuments");

    const seq = bundleData?.flowSequence || bundleData?.FlowSequence || null;
    const seqMap = new Map<string, number>();
    if (Array.isArray(seq)) {
      seq.forEach((s: any) => {
        const id = `${s?.FlowId ?? s?.flowId ?? ""}`.trim();
        const n = Number(s?.Sequence ?? s?.sequence);
        if (id && Number.isFinite(n)) {
          seqMap.set(id, n);
        }
      });
    }

    const flowsSequencedFlag = bundleData?.flowsSequenced ?? bundleData?.FlowsSequenced;
    let flowsSequencedForCreate: boolean;
    if (flowsSequencedFlag === false) {
      flowsSequencedForCreate = false;
    } else if (flowsSequencedFlag === true) {
      flowsSequencedForCreate = true;
    } else {
      flowsSequencedForCreate = seqMap.size > 0;
    }

    const bundleCoverPage = {
      url: bundleData.url,
      rowKey: bundleData.rowKey,
      description: bundleData.description,
      title: bundleData.title,
      type: bundleData.type,
      uniqueId: bundleData.uniqueId,
      price: bundleData.price,
      isPaid: bundleData.isPaid,
      customerEmail: bundleData.customerEmail,
      videoThumbNailUrl: bundleData.videoThumbNailUrl,
      flowsSequenced: flowsSequencedForCreate,
      enableNestedBundles:
        bundleData.enableNestedBundles === true || bundleData.EnableNestedBundles === true,
      categories: this.serializeCategories(bundleData),
      accessType: bundleData.accessType || bundleData.AccessType || "Public",
      bundleAccessType: this.resolveAccessType(bundleData),
    };

    localStorage.setItem("createbundledetails", JSON.stringify(bundleCoverPage));
    localStorage.setItem(
      "navigatebackfromcreatebundle",
      returnUrl || "/view-guide-pack/" + bundleData.rowKey
    );

    const shouldOrderFlows = seqMap.size > 0 && flowsSequencedFlag !== false;
    const orderedFlows = Array.isArray(bundleData.flows) ? [...bundleData.flows] : [];
    if (shouldOrderFlows) {
      orderedFlows.sort((a: any, b: any) => {
        const aSeq = seqMap.get(`${a?.rowKey ?? a?.RowKey ?? ""}`.trim());
        const bSeq = seqMap.get(`${b?.rowKey ?? b?.RowKey ?? ""}`.trim());
        if (aSeq == null && bSeq == null) return 0;
        if (aSeq == null) return 1;
        if (bSeq == null) return -1;
        return aSeq - bSeq;
      });
    }

    if (orderedFlows.length > 0) {
      localStorage.setItem(
        "addbundleselectedflows",
        JSON.stringify(orderedFlows.map((flow) => ({ flow, isSelected: true })))
      );
    }

    if (bundleData.flowSupportingDocumentsDocumentModels) {
      localStorage.setItem(
        "BundleSupportingDocumentDocuments",
        JSON.stringify(bundleData.flowSupportingDocumentsDocumentModels)
      );
    }

    if (bundleData.flowSupportingDocumentsHyperLinkModels) {
      localStorage.setItem(
        "BundleSupportingDocumentHyperLinksRequired",
        JSON.stringify(bundleData.flowSupportingDocumentsHyperLinkModels)
      );
    }

    // Tells the editor this state is exactly what's saved, so Save Draft starts disabled.
    localStorage.removeItem("createbundleSavedSnapshot");
    if (bundleData.rowKey) {
      localStorage.setItem("createbundleFreshEdit", `${bundleData.rowKey}`);
    }

    this.router.navigate(["/create-guide-pack"]);
  }

  private resolveAccessType(bundleData: any): string {
    const access = String(bundleData?.accessType || bundleData?.AccessType || "").toLowerCase();
    if (access === "private") return "Private";
    if (access === "restricted" || access === "linkaccess") return "LinkAccess";
    const flows = Array.isArray(bundleData?.flows) ? bundleData.flows : [];
    if (flows.length > 0) {
      const first = flows[0];
      const flowAccess = String(first?.accessType || first?.AccessType || "").toLowerCase();
      const restricted = String(
        first?.restrictedAccessType || first?.RestrictedAccessType || ""
      ).toLowerCase();
      if (flowAccess === "private") return "Private";
      if (flowAccess === "restricted" || restricted === "linkaccess") return "LinkAccess";
    }
    return "Public";
  }

  private serializeCategories(bundleData: any): any[] {
    const categories = bundleData?.categories || bundleData?.Categories || [];
    if (!Array.isArray(categories)) return [];
    return categories.map((c: any, index: number) => ({
      id: c.id || c.Id || `cat_${index}`,
      name: c.name || c.Name || "",
      order: c.order || c.Order || index + 1,
      nestedBundles: c.nestedBundles || c.NestedBundles || [],
      flows: c.flows || c.Flows || [],
      NestedBundleIds:
        c.nestedBundleIds ||
        c.NestedBundleIds ||
        (c.nestedBundles || c.NestedBundles || []).map((b: any) => b?.rowKey || b?.RowKey),
      FlowIds:
        c.flowIds || c.FlowIds || (c.flows || c.Flows || []).map((f: any) => f?.rowKey || f?.RowKey),
    }));
  }
}
